import { z } from 'zod';
import { IntellioAssistantArtifactActionDescriptorSchema } from '../entities/assistant';
import { JsonObjectSchema } from '../types/json';

export const AssistantActiveRouteFacetKindSchema = z.enum([
  'filters',
  'templates',
  'table',
  'chart',
  'selection',
  'artifact-workspace',
  'history',
  'export',
  'visualization3d',
]);
export type AssistantActiveRouteFacetKind = z.infer<typeof AssistantActiveRouteFacetKindSchema>;

export const AssistantActiveRouteFacetSchema = z.object({
  kind: AssistantActiveRouteFacetKindSchema,
  label: z.string().trim().min(1).nullable().optional(),
  summary: z.string().trim().min(1).nullable().optional(),
  available: z.boolean().default(true),
  itemCount: z.number().int().nonnegative().nullable().optional(),
  sourceRef: z.string().trim().min(1).nullable().optional(),
  state: JsonObjectSchema.optional(),
}).strict();
export type AssistantActiveRouteFacet = z.infer<typeof AssistantActiveRouteFacetSchema>;

export const AssistantActiveRouteArtifactSourceSchema = z.object({
  artifactType: z.string().trim().min(1),
  label: z.string().trim().min(1).nullable().optional(),
  sourceRef: z.string().trim().min(1).nullable().optional(),
  routeScoped: z.boolean().default(true),
  actions: z.array(IntellioAssistantArtifactActionDescriptorSchema).default([]),
}).strict();
export type AssistantActiveRouteArtifactSource = z.infer<typeof AssistantActiveRouteArtifactSourceSchema>;

export const AssistantActiveRouteCapabilityConstraintsSchema = z.object({
  allowedCapabilityIds: z.array(z.string().trim().min(1)).default([]),
  disabledCapabilityIds: z.array(z.string().trim().min(1)).default([]),
  allowedPolicyEventTypes: z.array(z.string().trim().min(1)).default([]),
  disabledPolicyEventTypes: z.array(z.string().trim().min(1)).default([]),
  allowLocalMutation: z.boolean().default(false),
  allowArtifactWorkspaceOpen: z.boolean().default(false),
  allowExport: z.boolean().default(false),
}).strict();
export type AssistantActiveRouteCapabilityConstraints = z.infer<
  typeof AssistantActiveRouteCapabilityConstraintsSchema
>;

export const AssistantActiveRouteVisibleStateSummarySchema = z.object({
  title: z.string().trim().min(1).nullable().optional(),
  summary: z.string().trim().min(1),
  metrics: z.array(z.object({
    label: z.string().trim().min(1),
    value: z.string().trim().min(1),
  }).strict()).default([]),
}).strict();
export type AssistantActiveRouteVisibleStateSummary = z.infer<
  typeof AssistantActiveRouteVisibleStateSummarySchema
>;

export const AssistantActiveRouteStalenessSchema = z.object({
  state: z.enum(['fresh', 'stale', 'unknown']).default('unknown'),
  capturedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  expiresAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  reason: z.string().trim().min(1).nullable().optional(),
}).strict();
export type AssistantActiveRouteStaleness = z.infer<typeof AssistantActiveRouteStalenessSchema>;

export const AssistantActiveRouteSourceRefSchema = z.object({
  sourceRef: z.string().trim().min(1),
  kind: z.string().trim().min(1).nullable().optional(),
  label: z.string().trim().min(1).nullable().optional(),
}).strict();
export type AssistantActiveRouteSourceRef = z.infer<typeof AssistantActiveRouteSourceRefSchema>;

export const AssistantActiveRouteContextEnvelopeSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  contextRef: z.string().trim().min(1),
  routeFamily: z.string().trim().min(1),
  pathname: z.string().trim().min(1),
  revision: z.string().trim().min(1),
  facets: z.array(AssistantActiveRouteFacetSchema).default([]),
  artifactSources: z.array(AssistantActiveRouteArtifactSourceSchema).default([]),
  capabilityConstraints: AssistantActiveRouteCapabilityConstraintsSchema.default({
    allowedCapabilityIds: [],
    disabledCapabilityIds: [],
    allowedPolicyEventTypes: [],
    disabledPolicyEventTypes: [],
    allowLocalMutation: false,
    allowArtifactWorkspaceOpen: false,
    allowExport: false,
  }),
  visibleStateSummary: AssistantActiveRouteVisibleStateSummarySchema,
  staleness: AssistantActiveRouteStalenessSchema.default({ state: 'unknown' }),
  sourceRefs: z.array(AssistantActiveRouteSourceRefSchema).default([]),
}).strict();
export type AssistantActiveRouteContextEnvelope = z.infer<
  typeof AssistantActiveRouteContextEnvelopeSchema
>;

export const AssistantPolicyEventSchema = z.object({
  eventId: z.string().trim().min(1),
  type: z.enum(['route_local_action', 'artifact_action']),
  policyEventType: z.string().trim().min(1),
  routeContextRef: z.string().trim().min(1).nullable().optional(),
  routeRevision: z.string().trim().min(1).nullable().optional(),
  routeFamily: z.string().trim().min(1).nullable().optional(),
  pathname: z.string().trim().min(1).nullable().optional(),
  capabilityId: z.string().trim().min(1).nullable().optional(),
  actionId: z.string().trim().min(1).nullable().optional(),
  artifactActionId: z.string().trim().min(1).nullable().optional(),
  sourceRef: z.string().trim().min(1).nullable().optional(),
  payload: JsonObjectSchema.default({}),
}).strict();
export type AssistantPolicyEvent = z.infer<typeof AssistantPolicyEventSchema>;

export const AssistantPolicyEventResultSchema = z.object({
  accepted: z.boolean(),
  status: z.enum(['accepted', 'completed', 'rejected', 'stale', 'blocked', 'failed']),
  message: z.string().trim().min(1),
  routeContextRef: z.string().trim().min(1).nullable().optional(),
  routeRevision: z.string().trim().min(1).nullable().optional(),
  presentation: JsonObjectSchema.nullable().optional(),
  details: z.string().trim().min(1).nullable().optional(),
}).strict();
export type AssistantPolicyEventResult = z.infer<typeof AssistantPolicyEventResultSchema>;
